/* Web Push subscription helpers — talks to /api/push/* routes and registers
 * /sw-push.js as the service worker. Designed to be safe to import on any
 * page (no-ops gracefully when the browser doesn't support Push).
 */
import { api } from "@/lib/api";

const SW_URL = "/sw-push.js";

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

async function registerSW() {
  const reg = await navigator.serviceWorker.register(SW_URL);
  await navigator.serviceWorker.ready;
  return reg;
}

/** Ask the OS for permission. Returns one of: "granted" | "denied" | "default". */
export async function requestPermission() {
  if (!("Notification" in window)) return "denied";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  return Notification.requestPermission();
}

/** Returns the live PushSubscription object (or null if not subscribed). */
export async function currentSubscription() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** Subscribe (or re-subscribe) the current browser and persist on the server. */
export async function subscribeToPush() {
  if (!pushSupported()) throw new Error("Push not supported in this browser.");
  const perm = await requestPermission();
  if (perm !== "granted") throw new Error("Notification permission was not granted.");

  const reg = await registerSW();

  // Pull the VAPID public key fresh each time — small payload, avoids stale-cache bugs
  const { data: keyResp } = await api.get(`/push/public-key`);
  const applicationServerKey = urlBase64ToUint8Array(keyResp.publicKey);

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  }

  const json = sub.toJSON();
  await api.post(`/push/subscribe`, {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });
  return sub;
}

/** Unsubscribe on both the browser and the server. */
export async function unsubscribeFromPush() {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;

  const json = sub.toJSON();
  try {
    await api.post(`/push/unsubscribe`, {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    });
  } catch (_) {
    /* server may not know it — that's fine */
  }
  return sub.unsubscribe();
}

export async function getPreferences() {
  const { data } = await api.get(`/push/preferences`);
  return data;
}

export async function updatePreferences(patch) {
  const { data } = await api.put(`/push/preferences`, patch);
  return data;
}

export async function testPush() {
  await api.post(`/push/test`, {});
}

/** Attach a listener for the in-app navigation messages the SW sends after a
 * notificationclick. Returns an unsubscribe fn. */
export function onPushNav(handler) {
  if (typeof navigator === "undefined" || !navigator.serviceWorker) return () => {};
  const fn = (event) => {
    if (event.data && event.data.type === "push-nav") handler(event.data.url);
  };
  navigator.serviceWorker.addEventListener("message", fn);
  return () => navigator.serviceWorker.removeEventListener("message", fn);
}
